/**
 * @file App-wide Firestore bootstrap and listener orchestration.
 */

import { signOut } from "https://www.gstatic.com/firebasejs/12.19.0/firebase-auth.js";
import {
    collection,
    doc,
    getDoc,
    limit,
    onSnapshot,
    orderBy,
    query,
    setDoc,
    where,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";
import { auth, db } from "../firebase/firebase.js";
import {
    activeListeners,
    appData,
    loadViewSettings,
    setDataListenersAttachedStatus,
    state,
    updateGlobalTasks,
} from "../state/state.js";
import {
    enforceNonAdminMaintenanceState,
    forceMaintenanceSignOut,
} from "../auth/sessionGuards.js";
import { detachDetailViewListeners } from "./detailListeners.js";
import { renderHeader } from "../shell/appHeader.js";
import {
    renderCurrentBoard,
} from "../view/viewController.js";
import { renderDetailsView } from "../detail/detailController.js";
import {
    getBoardRegistryEntry,
    getBoardRegistryEntryByItemType,
} from "../../shared/board/boardRegistry.js";
import {
    getItemType,
} from "../../shared/utils/itemLookup.js";
import {
    renderNotificationShade,
    showToast,
} from "../notifications/notificationController.js";
import {
    isRetiredNotificationEvent,
} from "../notifications/notificationRegistryAdapter.js";
import { claimUserInvite } from "../../domains/settings/userLifecycleApi.js";
import { attachDrugDataListeners } from "../../domains/drugs/drugDataListeners.js";
import { detachRecallsRouteListeners } from "../../domains/recalls/recallsDataListeners.js";
import { detachCalendarRouteListeners } from "../../domains/calendar/calendarDataListeners.js";
import {
    resetUserInvitesListenerState,
    syncUserInvitesListener,
} from "../../domains/settings/userInviteListeners.js";
import {
    assembleRuntimeConfig,
    getConfigFallback,
} from "./configAssembler.js";
import {
    buildMyTaskQueries,
} from "./taskAccessQueries.js";
import { attachParentCollectionListeners } from "./parentBoardListeners.js";

const CLOSED_ITEMS_LIMIT = 30;

function sortByUpdatedAtDesc(items = []) {
    return [...items].sort((a, b) => getUpdatedAtValue(b) - getUpdatedAtValue(a));
}

/**
 * Loads the initial config documents before live listeners attach.
 * @returns {Promise<object>} Config data or fallback config.
 */
export async function loadInitialConfig() {
    try {
        const configSnapshot = await getDoc(doc(db, "config", "main"));
        if (!configSnapshot.exists()) {
            console.error("Firestore Error: Config document 'config/main' not found!");
            return getConfigFallback();
        }

        let notificationConfig = null;
        try {
            const notificationConfigSnapshot = await getDoc(
                doc(db, "config", "notifications"),
            );
            notificationConfig = notificationConfigSnapshot.exists() ?
                notificationConfigSnapshot.data() : null;
        } catch (error) {
            console.warn("Notification config unavailable; using safe notification defaults.", error);
        }

        return assembleRuntimeConfig(
            configSnapshot.data(),
            notificationConfig,
        );
    } catch (error) {
        console.error("Error loading config document:", error);
        return getConfigFallback();
    }
}

async function ensureCurrentUserProfile(user) {
    const userRef = doc(db, "users", user.uid);
    let snapshot = await getDoc(userRef);
    if (snapshot.exists()) {
        return { id: snapshot.id, ...snapshot.data() };
    }

    await setDoc(userRef, {
        name: user.displayName || "New User",
        email: user.email || "",
        role: "user",
        permissions: [],
        managerUserId: "none",
        isActive: true,
        isAppProvider: false,
    }, { merge: true });

    snapshot = await getDoc(userRef);
    if (!snapshot.exists()) {
        throw new Error("Current user profile was not available after self-create.");
    }

    return { id: snapshot.id, ...snapshot.data() };
}

async function applyInviteClaimIfPresent(user, currentProfile) {
    if (!user?.uid) {
        return currentProfile;
    }

    try {
        await user.reload();
        await user.getIdToken(true);

        const claimResult = await claimUserInvite({
            name: user.displayName || currentProfile?.name || "",
        });

        if (!claimResult?.applied) {
            return currentProfile;
        }

        const refreshedSnapshot = await getDoc(doc(db, "users", user.uid));
        if (!refreshedSnapshot.exists()) {
            return currentProfile;
        }

        return { id: refreshedSnapshot.id, ...refreshedSnapshot.data() };
    } catch (error) {
        console.warn("Invite claim skipped during auth bootstrap:", error);
        return currentProfile;
    }
}

function getUpdatedAtValue(item) {
    const updatedAt = item?.updatedAt;
    if (!updatedAt) {return 0;}

    if (typeof updatedAt?.toMillis === "function") {
        return updatedAt.toMillis();
    }
    if (typeof updatedAt?.seconds === "number") {
        const nanos = typeof updatedAt?.nanoseconds === "number" ? updatedAt.nanoseconds : 0;
        return (updatedAt.seconds * 1000) + Math.floor(nanos / 1000000);
    }

    const parsed = new Date(updatedAt);
    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
}

function mergeUniqueById(...collections) {
    const merged = new Map();
    collections.flat().forEach((item) => {
        if (!item?.id) {return;}

        const existing = merged.get(item.id);
        if (!existing || getUpdatedAtValue(item) >= getUpdatedAtValue(existing)) {
            merged.set(item.id, item);
        }
    });

    return Array.from(merged.values());
}

function attachBootstrapListeners(guardOptions) {
    let mainConfig = appData.config || getConfigFallback();
    let notificationConfig = null;

    const applyRuntimeConfig = () => {
        appData.config = assembleRuntimeConfig(mainConfig, notificationConfig);
    };

    const configRef = doc(db, "config", "main");
    const configListener = onSnapshot(configRef, (configDoc) => {
        mainConfig = configDoc.exists() ? configDoc.data() : getConfigFallback();
        applyRuntimeConfig();
        if (enforceNonAdminMaintenanceState(guardOptions)) {return;}
        if (state.currentUserProfile) {renderHeader();}
    }, (error) => {
        console.error("Error listening to config document:", error);
        mainConfig = getConfigFallback();
        applyRuntimeConfig();
    });
    activeListeners.push(configListener);

    const notificationConfigRef = doc(db, "config", "notifications");
    const notificationConfigListener = onSnapshot(notificationConfigRef, (configDoc) => {
        notificationConfig = configDoc.exists() ? configDoc.data() : null;
        applyRuntimeConfig();
        if (state.currentUserProfile) {renderHeader();}
        if (state.isNotificationShadeOpen) {renderNotificationShade();}
    }, (error) => {
        console.error("Error listening to notification config document:", error);
        notificationConfig = null;
        applyRuntimeConfig();
    });
    activeListeners.push(notificationConfigListener);

    const currentUserRef = doc(db, "users", state.currentUser.uid);
    const currentUserListener = onSnapshot(currentUserRef, (snapshot) => {
        if (!snapshot.exists()) {return;}
        state.currentUserProfile = { id: snapshot.id, ...snapshot.data() };
        if (state.currentUserProfile?.isActive === false) {
            forceMaintenanceSignOut(
                "This account is inactive. Please contact an administrator.",
                guardOptions,
            );
            return;
        }
        syncUserInvitesListener(state.currentUserProfile?.role === "admin");
        if (enforceNonAdminMaintenanceState(guardOptions)) {return;}
        renderHeader();
    }, (error) => {
        console.error("Error listening to current user profile:", error);
    });
    activeListeners.push(currentUserListener);

    const usersRef = collection(db, "users");
    const usersListener = onSnapshot(usersRef, (snapshot) => {
        appData.users = snapshot.docs.map((snapshotDoc) => ({ id: snapshotDoc.id, ...snapshotDoc.data() }));
        if (state.currentUserProfile) {renderHeader();}
    }, (error) => {
        console.error("Error listening to users collection:", error);
    });
    activeListeners.push(usersListener);
}

function attachNotificationsListener(user) {
    let initialNotificationsLoaded = false;
    const notifsQuery = query(
        collection(db, "notifications"),
        where("userId", "==", user.uid),
        where("read", "==", false),
        orderBy("createdAt", "desc"),
    );

    const unsubscribeNotifications = onSnapshot(
        notifsQuery,
        (notificationSnapshot) => {
            const controls = appData.config?.adminNotificationControls;
            const defaults = appData.config?.adminNotificationDefaults;
            const userSettings = state.currentUserProfile?.notificationSettings || {};

            const resolveSetting = (event, type) => {
                if (isRetiredNotificationEvent(event)) {return false;}

                if (event.startsWith("team_")) {
                    const teamSettings = appData.config?.teamNotificationSettings?.[event];
                    return teamSettings ? teamSettings[type] === true : false;
                }

                if (!controls || !defaults) {return false;}
                const control = controls[event];
                const defaultSetting = defaults[event];
                if (!control || !defaultSetting) {return false;}

                if (control.visibility === "hidden") {
                    return defaultSetting[type];
                }
                if (control[type] === "locked_on") {
                    return true;
                }
                if (control[type] === "locked_off") {
                    return false;
                }
                const userPref = userSettings[event]?.[type];
                if (userPref !== undefined) {
                    return userPref;
                }
                return defaultSetting[type];
            };

            const allUnread = notificationSnapshot.docs.map((snapshotDoc) => ({
                id: snapshotDoc.id,
                ...snapshotDoc.data(),
            }));

            appData.notifications = allUnread.filter((notification) => (
                resolveSetting(notification.triggerEvent, "inApp")
            ));

            document.querySelectorAll(".notification-bell-btn").forEach((bellBtn) => {
                bellBtn.classList.toggle(
                    "has-unread",
                    appData.notifications.length > 0,
                );
            });

            notificationSnapshot.docChanges().forEach((change) => {
                if (change.type === "added" && initialNotificationsLoaded) {
                    const newNotification = { id: change.doc.id, ...change.doc.data() };
                    if (resolveSetting(newNotification.triggerEvent, "inApp")) {
                        showToast(newNotification);
                    }
                }
            });

            if (state.isNotificationShadeOpen) {
                renderNotificationShade();
            }

            initialNotificationsLoaded = true;
        },
        (error) => {
            console.error("Error fetching notifications: ", error);
            appData.notifications = [];
            document.querySelectorAll(".notification-bell-btn").forEach((bellBtn) => {
                bellBtn.classList.remove("has-unread");
            });
            if (state.isNotificationShadeOpen) {
                renderNotificationShade();
            }
        },
    );
    activeListeners.push(unsubscribeNotifications);
}

function attachChecklistTemplateListener() {
    const templatesQuery = query(
        collection(db, "checklist_templates"),
        orderBy("sortOrder", "asc"),
        limit(1000),
    );
    const templatesListener = onSnapshot(templatesQuery, snapshot => {
        appData.checklistTemplates = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        window.dispatchEvent(new CustomEvent("checklistTemplatesUpdated"));
    }, error => console.error("Error listening to checklist_templates:", error));
    activeListeners.push(templatesListener);
}

function attachTaskListeners(user) {
    const taskBuckets = {
        open: new Map(),
        closed: new Map(),
    };

    const triggerTaskUpdateAndRender = () => {
        const paginationState = state.closedItemsPagination.tasks;
        const closedBuffer = sortByUpdatedAtDesc(mergeUniqueById(
            ...taskBuckets.closed.values(),
            paginationState.extraItems || [],
        ));
        const visibleCount = Number.isFinite(paginationState.visibleCount) ?
            paginationState.visibleCount :
            CLOSED_ITEMS_LIMIT;
        const visibleClosedTasks = closedBuffer.slice(0, visibleCount);
        paginationState.bufferItems = closedBuffer;
        paginationState.bufferHasMore = closedBuffer.length > visibleClosedTasks.length;
        paginationState.hasMore = paginationState.bufferHasMore ||
            paginationState.ownerHasMore ||
            paginationState.assignedHasMore;

        const newTasks = mergeUniqueById(
            ...taskBuckets.open.values(),
            visibleClosedTasks,
        );

        updateGlobalTasks(newTasks);
        if (!state.selectedItemId) {
            const currentBoard = getBoardRegistryEntry(state.currentView);
            if (
                state.currentView === "tasks" ||
                currentBoard?.capabilities?.supportsTasks
            ) {
                renderCurrentBoard();
            }
        } else if (state.selectedItemId) {
            const currentItemType = getItemType(state.selectedItemId);
            const selectedBoard = getBoardRegistryEntryByItemType(currentItemType);
            if (
                currentItemType === "task" ||
                selectedBoard?.capabilities?.supportsTasks
            ) {
                const isChecklistEditing = document.activeElement &&
                    document.activeElement.matches(".checklist-item-text-input, .checklist-ghost-input");

                if (!isChecklistEditing) {
                    renderDetailsView();
                }
            }
        }
    };

    const paginationState = state.closedItemsPagination.tasks;
    paginationState.taskQueryScopes = {};
    paginationState.ownerHasMore = false;
    paginationState.assignedHasMore = false;
    paginationState.hasMore = false;
    paginationState.ownerLastDoc = null;
    paginationState.assignedLastDoc = null;
    paginationState.hasLoadedMore = false;
    paginationState.extraItems = [];
    paginationState.bufferItems = [];
    paginationState.bufferHasMore = false;
    paginationState.visibleCount = CLOSED_ITEMS_LIMIT;

    const updateClosedPaginationState = (spec, snapshot) => {
        const taskPaginationState = state.closedItemsPagination.tasks;
        if (!taskPaginationState.hasLoadedMore) {
            taskPaginationState.taskQueryScopes[spec.key] = {
                assignment: spec.assignment,
                scopeKey: spec.scopeKey,
                scopeType: spec.scopeType,
                parentType: spec.parentType,
                lastDoc: snapshot.docs.length > 0 ?
                    snapshot.docs[snapshot.docs.length - 1] :
                    null,
                hasMore: snapshot.docs.length === CLOSED_ITEMS_LIMIT,
            };
            const scopeStates = Object.values(
                taskPaginationState.taskQueryScopes,
            );
            taskPaginationState.ownerHasMore = scopeStates.some((scope) =>
                scope.assignment === "owner" && scope.hasMore);
            taskPaginationState.assignedHasMore = scopeStates.some((scope) =>
                scope.assignment === "assigned" && scope.hasMore);
            taskPaginationState.hasMore =
                scopeStates.some((scope) => scope.hasMore);
        }
    };

    buildMyTaskQueries({
        userId: user.uid,
        userProfile: state.currentUserProfile,
        isClosed: false,
    }).forEach((spec) => {
        const tasksListener = onSnapshot(spec.queryRef, snapshot => {
            taskBuckets.open.set(
                spec.key,
                snapshot.docs.map(d => ({ id: d.id, ...d.data() })),
            );
            triggerTaskUpdateAndRender();
        }, error => {
            console.error(`Error listening to open ${spec.key} tasks:`, error);
            taskBuckets.open.set(spec.key, []);
            triggerTaskUpdateAndRender();
        });
        activeListeners.push(tasksListener);
    });

    buildMyTaskQueries({
        userId: user.uid,
        userProfile: state.currentUserProfile,
        isClosed: true,
        limitCount: CLOSED_ITEMS_LIMIT,
    }).forEach((spec) => {
        const tasksListener = onSnapshot(spec.queryRef, snapshot => {
            taskBuckets.closed.set(
                spec.key,
                snapshot.docs.map(d => ({ id: d.id, ...d.data() })),
            );
            updateClosedPaginationState(spec, snapshot);
            triggerTaskUpdateAndRender();
        }, error => {
            console.error(`Error listening to closed ${spec.key} tasks:`, error);
            taskBuckets.closed.set(spec.key, []);
            triggerTaskUpdateAndRender();
        });
        activeListeners.push(tasksListener);
    });
}

function attachTeamsListener() {
    const teamsQuery = query(collection(db, "teams"));
    const teamsListener = onSnapshot(teamsQuery, snapshot => {
        appData.teams = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        import("../../domains/settings/settingsController.js")
            .then((module) => module.syncTeamsSettingsFromLiveData?.())
            .catch((error) => console.error("Error syncing live teams:", error));
    }, error => console.error("Error listening to teams:", error));
    activeListeners.push(teamsListener);
}

function attachPatientCounterListener() {
    const counterRef = doc(db, "counters", "patient_case_files");
    const unsubCounter = onSnapshot(counterRef, (docSnap) => {
        if (docSnap.exists()) {
            const count = docSnap.data().count || 0;
            if (!appData.config) {appData.config = {};}
            appData.config.nextCaseFileNumber = count + 1;
        }
    }, (error) => {
        console.error("Error listening to patient counter:", error);
    });
    activeListeners.push(unsubCounter);
}

function attachDataListeners(user) {
    if (!user || !user.uid) {
        console.error("Cannot attach data listeners: invalid user object.");
        return;
    }

    attachTeamsListener();
    attachParentCollectionListeners(CLOSED_ITEMS_LIMIT);
    attachChecklistTemplateListener();
    attachTaskListeners(user);
    attachDrugDataListeners();
    attachPatientCounterListener();
}

/**
 * Prepares app data and listeners after a user signs in.
 * @param {object} user Firebase Auth user.
 * @param {object} options Session callbacks.
 * @param {Function} options.queueLoginNotice Stores login shell notice.
 * @param {Function} options.showApp Renders the signed-in app shell.
 * @param {Function} options.renderSignedOutShell Fallback signed-out renderer.
 * @param {Function} options.restorePostAuthDestination Restores destination.
 */
export async function initializeAppWithUser(user, options) {
    loadViewSettings();
    activeListeners.forEach((unsubscribe) => {
        try { unsubscribe(); } catch (error) { console.warn("Error unsubscribing listener:", error); }
    });
    activeListeners.length = 0;
    resetUserInvitesListenerState();
    setDataListenersAttachedStatus(false);
    detachDetailViewListeners();
    detachRecallsRouteListeners();
    detachCalendarRouteListeners();

    const guardOptions = {
        queueLoginNotice: options.queueLoginNotice,
        onFallback: options.renderSignedOutShell,
    };

    try {
        appData.config = await loadInitialConfig();
        state.currentUserProfile = await ensureCurrentUserProfile(user);
        state.currentUserProfile = await applyInviteClaimIfPresent(
            user,
            state.currentUserProfile,
        );

        if (state.currentUserProfile?.isActive === false) {
            options.queueLoginNotice("This account is inactive. Please contact an administrator.");
            await signOut(auth);
            return;
        }

        if (enforceNonAdminMaintenanceState(guardOptions)) {return;}

        attachBootstrapListeners(guardOptions);
        syncUserInvitesListener(state.currentUserProfile?.role === "admin");
        attachDataListeners(user);
        attachNotificationsListener(user);
        setDataListenersAttachedStatus(true);
        options.showApp();
        await options.restorePostAuthDestination();
        renderHeader();
    } catch (error) {
        console.error("Error during session bootstrap:", error);
        options.queueLoginNotice("Your session could not be prepared. Please try again.");
        await signOut(auth);
    }
}
